import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/*
  Min i max funkcji kwadratowej na przedziale domkniętym [L, R] (2 punkty).

  Schemat:
  1) Liczymy x_w = -b/(2a).
  2) Liczymy f(L), f(R) oraz dodatkowo f(x_w), jeśli x_w ∈ (L, R).
  3) Min i max na [L,R] to min/max z tych wartości.

  Losowanie:
  - 50%: x_w należy do (L, R) (wierzchołek w środku, NIE na brzegu)
  - 50%: x_w nie należy do [L, R]
*/

/* =========================
   Helpers
========================= */

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickByWeight(items) {
  const total = items.reduce((s, it) => s + (it.w ?? 1), 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= (it.w ?? 1);
    if (r <= 0) return it.v;
  }
  return items[items.length - 1].v;
}

function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (raw === "") return null;

  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }

  if (/^[+\-]?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function termAx2(a) {
  if (a === 1) return "x^2";
  if (a === -1) return "-x^2";
  return `${a}x^2`;
}
function termBx(b) {
  if (b === 0) return null;
  const abs = Math.abs(b);
  const core = abs === 1 ? "x" : `${abs}x`;
  return { sign: b > 0 ? "+" : "-", core };
}
function termC(c) {
  if (c === 0) return null;
  return { sign: c > 0 ? "+" : "-", core: String(Math.abs(c)) };
}
function wrapInt(n) {
  return n < 0 ? `(${n})` : String(n);
}

/* =========================
   Generator
========================= */

const CASES = [
  { v: "IN_STRICT", w: 1 }, // x_w w (L,R)
  { v: "OUT", w: 1 },       // x_w poza [L,R]
];

export function generateTask() {
  // a ≠ 0
  const aOptions = [-3, -2, -1, 1, 2, 3];
  const a = aOptions[randInt(0, aOptions.length - 1)];

  // Ładny wierzchołek: x_w = k (całkowity)
  const k = randInt(-5, 5) || 2;
  const b = -2 * a * k;

  // c w sensownym zakresie
  const c = randInt(-12, 12);

  const xW = k;
  const caseType = pickByWeight(CASES);

  let L, R;

  if (caseType === "IN_STRICT") {
    // chcemy L < k < R
    const leftPad = randInt(1, 5);
    const rightPad = randInt(1, 5);
    L = k - leftPad;
    R = k + rightPad;

    // gwarancja: L < k < R (leftPad/rightPad >= 1)
  } else {
    // chcemy k NIE należał do [L, R]
    const side = Math.random() < 0.5 ? "LEFT" : "RIGHT";
    if (side === "LEFT") {
      // R < k
      const width = randInt(2, 6);
      R = k - randInt(1, 4);
      L = R - width;
    } else {
      // L > k
      const width = randInt(2, 6);
      L = k + randInt(1, 4);
      R = L + width;
    }
  }

  const f = (x) => a * x * x + b * x + c;

  const fL = f(L);
  const fR = f(R);
  const inStrict = xW > L && xW < R;
  const fW = inStrict ? f(xW) : null;

  const candidates = [
    { x: L, y: fL },
    { x: R, y: fR },
    ...(inStrict ? [{ x: xW, y: fW }] : []),
  ];

  let minCand = candidates[0];
  let maxCand = candidates[0];
  for (const cand of candidates.slice(1)) {
    if (cand.y < minCand.y) minCand = cand;
    if (cand.y > maxCand.y) maxCand = cand;
  }

  return {
    type: "QuadraticExtremeOnIntervalTask",
    data: {
      a,
      b,
      c,
      L,
      R,
      xW,
      inStrict,
      fL,
      fR,
      fW,
      minY: minCand.y,
      maxY: maxCand.y,
      minAtX: minCand.x,
      maxAtX: maxCand.x,
      maxPoints: 2,
    },
  };
}

/* =========================
   Treść
========================= */

export function renderTask({ data }) {
  const { a, b, c, L, R } = data;

  const bx = termBx(b);
  const cc = termC(c);

  const parts = [termAx2(a)];
  if (bx) parts.push(`${bx.sign} ${bx.core}`);
  if (cc) parts.push(`${cc.sign} ${cc.core}`);

  const polyLatex = parts.join(" ");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p><strong>Zadanie optymalizacyjne.</strong></p>

      <p>
        Dana jest funkcja <InlineMath math={'f'} /> określona wzorem:
      </p>

      <BlockMath math={`f(x) = ${polyLatex}.`} />

      <p>
        Wyznacz <strong>najmniejszą</strong> oraz <strong>największą</strong>{" "}
        wartość funkcji na przedziale{" "}
        <InlineMath math={`[${L},\\,${R}]`} />.
      </p>
    </div>
  );
}

/* =========================
   Input
========================= */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (v) => onChange({ ...(ua || {}), [key]: v });

  return (
    <div style={{ display: "grid", rowGap: 14, maxWidth: 360 }}>
      <div style={{ display: "grid", rowGap: 8 }}>
        <div>Podaj <strong>najmniejszą</strong> wartość na danym przedziale:</div>
        <FractionInput
          value={ua.yMin}
          onChange={setField("yMin")}
          disabled={disabled}
          label={"y_{\\min}"}   // <- to poprawi podgląd w samym FractionInput
        />
      </div>

      <div style={{ display: "grid", rowGap: 8 }}>
        <div>Podaj <strong>największą</strong> wartość na danym przedziale:</div>
        <FractionInput
          value={ua.yMax}
          onChange={setField("yMax")}
          disabled={disabled}
          label={"y_{\\max}"}   // <- to poprawi podgląd w samym FractionInput
        />
      </div>
    </div>
  );
}


/* =========================
   Podgląd odpowiedzi
========================= */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const rawMin =
    typeof userAnswer.yMin === "object"
      ? userAnswer.yMin?.raw ?? ""
      : String(userAnswer.yMin ?? "");

  const rawMax =
    typeof userAnswer.yMax === "object"
      ? userAnswer.yMax?.raw ?? ""
      : String(userAnswer.yMax ?? "");

  const latexMin = rawMin || "\\text{–}";
  const latexMax = rawMax || "\\text{–}";

  return (
    <div style={{ display: "grid", rowGap: 4 }}>
      <div><InlineMath math={`y_{\\min} = ${latexMin}`} /></div>
      <div><InlineMath math={`y_{\\max} = ${latexMax}`} /></div>
    </div>
  );
}

/* =========================
   Check (2 punkty, częściowe)
========================= */

export function checkAnswer({ data }, userAnswer) {
  const vMin = parseNumber(userAnswer?.yMin);
  const vMax = parseNumber(userAnswer?.yMax);

  const minOk = vMin != null && Math.abs(vMin - data.minY) < 1e-9;
  const maxOk = vMax != null && Math.abs(vMax - data.maxY) < 1e-9;

  const parts = { min: minOk, max: maxOk };
  const score = (minOk ? 1 : 0) + (maxOk ? 1 : 0);

  return { isCorrect: score === 2, score, maxScore: 2, parts };
}

/* =========================
   Poprawna odpowiedź
========================= */

export function renderAnswer({ data }) {
  const { minY, maxY } = data;
  return (
    <div style={{ textAlign: "left", display: "grid", rowGap: 4 }}>
      <div><InlineMath math={`y_{\\min} = ${minY}`} /></div>
      <div><InlineMath math={`y_{\\max} = ${maxY}`} /></div>
    </div>
  );
}

/* =========================
   Wyjaśnienie
========================= */

export function renderExplanation({ data }) {
  const { a, b, c, L, R, xW, inStrict, fL, fR, fW, minY, maxY } = data;

  const aW = wrapInt(a);
  const bW = wrapInt(b);
  const cW = wrapInt(c);

  const stepX1 = String.raw`x_w=-\dfrac{b}{2a}.`;
  const stepX2 = String.raw`x_w=-\dfrac{${bW}}{2\cdot ${aW}}=${xW}.`;

  const fx = (x, y) =>
    String.raw`f(${x})=${aW}\cdot(${x})^2+${bW}\cdot(${x})+${cW}=${y}.`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1.</strong> Liczymy współrzędną <InlineMath math={'x_w'} /> wierzchołka:
      </div>
      <BlockMath math={stepX1} />
      <BlockMath math={stepX2} />

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2.</strong> Na przedziale domkniętym{" "}
        <InlineMath math={`[${L},\\,${R}]`} /> sprawdzamy wartości funkcji na końcach,
        a jeśli <InlineMath math={'x_w'} /> leży w środku przedziału, to także w wierzchołku.
      </div>

      <BlockMath math={fx(L, fL)} />
      <BlockMath math={fx(R, fR)} />

      {inStrict ? (
        <>
          <BlockMath math={fx(xW, fW)} />
          <div style={{ marginTop: 8 }}>
            Ponieważ <InlineMath math={`${L}<x_w<${R}`} />, porównujemy trzy wartości:
            <InlineMath math={`\\ f(${L}),\\ f(${R}),\\ f(x_w)`} />.
          </div>
        </>
      ) : (
        <div style={{ marginTop: 8 }}>
          Ponieważ <InlineMath math={`x_w\\notin[${L},\\,${R}]`} />, wystarczy porównać tylko
          <InlineMath math={`\\ f(${L})\\ \\text{i}\\ f(${R})`} />.
        </div>
      )}

      <div style={{ marginTop: 10, display: "grid", rowGap: 4 }}>
        <div>
          <strong>Wniosek:</strong> <InlineMath math={`y_{\\min}=${minY}`} />
        </div>
        <div>
          <strong>Wniosek:</strong> <InlineMath math={`y_{\\max}=${maxY}`} />
        </div>
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
